import { useMutation, useQuery } from "@tanstack/react-query";
import { bonusApi } from "@/services/api.service.ts";
import { RewardTypeEnum } from "@/pages/Bonus/components/LoginBonus/enums.tsx";

export type BonusLogo = {
  id: string;
  caption: string;
  logoUrl: string;
};

export type AdminBonusSetup = {
  id: string;
  startDate: string;
  endDate: string;
  rewardName: string;
  rewardType: number;
  bonusName: string;
  bonusCategory: number;
  bonusCategoryName: string;
  providerName: string;
  providerCategory: string;
  applyType: number;
  budget: number;
  value: number;
  logoUrl: string;
  unit: string;
};

export type PaginateAdminBonusSetup = {
  data: AdminBonusSetup[];
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  currentPage: number;
};

export type BonusProfileResp = {
  id: string;
  username: string;
  totalCashbackCode: number;
  totalDailyTicket: number;
  totalFiat: number;
  totalFreeBet: number;
  totalFreeSpin: number;
  totalMonthlyTicket: number;
  totalToken: number;
  totalWeeklyTicket: number;
  claimProgress: number;
};

export type BonusResp = {
  id: string;
  name: string;
  bonus: string;
  value: number;
  unit: string;
  additionalInfo: string;
  isSpecial: boolean;
  isClaim: boolean;
  imageUrl: string;
  bonusCategory: number;
  bonusCategoryName: string;
  rewardType: number;
  rewardTypeName: string;
  claimedAt: string;
};
export type PlayerBonusesResp = {
  id: string;
  userId: string;
  bonus: {
    id: string;
    bonusCode: string;
    bonusName: string;
    bonusType: number;
    bonusCategory: number;
    rewardType: number;
    value: number;
    unit: string;
    budget: number;
    createdAt: string;
    updatedAt: string;
  };
  bonusId: string;
  status: number;
  total: number;
  used: number;
  target: number;
  progress: number;
  createdAt: string;
  updatedAt: string;
};

export type PaginatePlayerBonusesResp = {
  data: PlayerBonusesResp[];
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  currentPage: number;
};

export enum BonusServiceEnum {
  "SHARK" = 1,
}

export type ClaimBonusPayload = {
  bonusId?: string;
  service: BonusServiceEnum;
};

export type CreateBonusPayload = {
  id: string;
  bonusName: string;
  bonusCategory: number;
  rewardType: number;
  applyType: number;
  startDate: string;
  endDate: string;
  logoUrl: string;
  value: number;
  budget: number;
  providerName?: string;
  providerCategory?: string;
};

export type CreateBonusUserPayload = {
  id: string;
  username: string;
  domain: number;
};

export const useGetBonusProfile = () => {
  return useQuery({
    queryKey: ["get-bonus-profile"],
    queryFn: async () => {
      const response = await bonusApi.get("/profile");
      return response.data;
    },
  });
};

export const useGetLoginBonus = () => {
  return useQuery({
    queryKey: ["get-login-bonus"],
    queryFn: async () => {
      const response = await bonusApi.get("/login-bonus");
      return response.data;
    },
  });
};

export const useClaimBonus = () => {
  return useMutation({
    mutationFn: (payload: ClaimBonusPayload) =>
      bonusApi.post("/claim", payload),
  });
};

export const useCreateBonus = () => {
  return useMutation({
    mutationFn: (bonusData: CreateBonusPayload) =>
      bonusApi.put("/bonus", bonusData),
  });
};

export const useCreateBonusProfile = () => {
  return useMutation({
    mutationFn: (payload: CreateBonusUserPayload) =>
      bonusApi.post("/user/create", payload),
  });
};

export const useGetAdminBonusSetup = (
  page = 1,
  pageSize = 10,
  rewardType: RewardTypeEnum = 0,
  month = "",
  date = "",
  id = "",
) => {
  const config = {
    params: {
      page,
      pageSize,
      rewardType,
      month,
      date,
      id,
    },
  };
  return useQuery({
    queryKey: [
      "get-admin-bonus-setup",
      page,
      pageSize,
      rewardType,
      month,
      date,
      id,
    ],
    queryFn: async () => {
      const response = await bonusApi.get("/bonus", config);
      return response.data;
    },
  });
};

export const useGetPlayerBonuses = (
  userId = "",
  page = 1,
  pageSize = 10,
  rewardType: RewardTypeEnum = 0,
  date = "",
) => {
  const config = {
    params: {
      page,
      pageSize,
      rewardType,
      date,
    },
  };
  return useQuery({
    queryKey: ["get-player-bonus", userId, page, pageSize, rewardType, date],
    queryFn: async () => {
      const response = await bonusApi.get(`/player/${userId}/claim`, config);
      return response.data;
    },
  });
};

export const useGetBonusLogo = () => {
  return useQuery({
    queryKey: ["get-bonus-logo"],
    queryFn: async () => {
      const response = await bonusApi.get("/logo");
      return response.data;
    },
  });
};
